import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {useParams, useHistory, useLocation} from 'react-router-dom';

import Grid from '@material-ui/core/Grid';
import Layout from './Layout';
import LayoutHeader, {LayoutHeaderIconTypes} from 'components/LayoutHeader';
import Container from 'components/Container';
import SiteSearchSelectionPrompt from 'components/SiteIDSearch/components/SiteSearchSelectionPrompt';
import SiteSearchResultsTable from 'features/SiteSearch/components/SiteSearchResultsTable';
import useDevice from 'features/SiteSearch/hooks/useDevice';
import {DeviceWithLocation} from 'features/SiteSearch/interfaces/DeviceResponse';
import ViewSelectionButton from 'features/SiteSearch/components/ViewSelectionButton';
import SGBody from 'components/Typography/SGBody';
import getSwitchURL from 'features/SiteSearch/functions/getSwitchURL';
import {useSiteState} from 'hooks/useSiteInfo/SiteStateProvider';
import {Site} from 'hooks/useSiteInfo/interfaces/Site';
import SiteType from 'hooks/useSiteInfo/interfaces/SiteType';

const StyledHeader = styled.div`
  display: grid;
  grid-template-areas:
    'headerTitle search'
    'headerSubtitle search';
  grid-template-columns: auto 550px;
  #header-text {
    grid-area: headerTitle;
  }
  #header-subtitle {
    grid-area: headerSubtitle;
    align-self: flex-end;
  }
  #siteid-search-container {
    grid-area: search;
  }
`;

const StyledSGBody = styled(SGBody)`
  color: ${props => props.theme.activeStateBlue};
`;

function SiteSearch() {
  const {urlKey} = useParams<any>();
  const location = useLocation();
  let history = useHistory();

  const [siteList, setSiteList] = useState<Site[]>([]);
  const [tableData, setTableData] = useState<DeviceWithLocation[]>([]);
  const [selectedDevices, setSelectedDevices] = useState<DeviceWithLocation[]>([]);

  const {data, pending, dataLoaded} = useDevice(siteList[0]?.siteId);

  //#region context
  const {
    state: {isSiteStateReady, sites},
  } = useSiteState();
  //#endregion Redux

  const handleSiteSelection = (siteId: Site) => {
    const params = [`siteId=${siteId.siteId}`];
    history.push(`/site-search/${urlKey}?${params.join('&')}`);
    setSiteList([siteId]);
  };

  const handleDeviceSelection = (deviceName: string[]) => {
    const matchingDevice = tableData.filter(tableDevice =>
      deviceName.includes(tableDevice.deviceName)
    );

    if (matchingDevice) {
      setSelectedDevices(matchingDevice);
    }
  };

  useEffect(() => {
    // if user has bookmarked a siteId use that to filter the siteList
    const searchParams = new URLSearchParams(location.search);
    if (!isSiteStateReady) {
      return;
    }
    if (searchParams.has('siteId')) {
      const allParamSiteIds = searchParams.getAll('siteId');
      const matchingSites = sites.filter(site => allParamSiteIds.includes(site.siteId));

      setSiteList(matchingSites);
    }
  }, [location, isSiteStateReady, sites]);

  useEffect(() => {
    if (siteList.length > 0 && !pending && data !== null) {
      const mergedSiteDevice = data.map(device => {
        const matchingSite = siteList.find(site => site.siteId === device.siteId);
        let locationArray: string[] = [];
        if (matchingSite) {
          matchingSite?.siteName !== undefined && locationArray.push(matchingSite.siteName);
          matchingSite?.address?.city !== undefined &&
            locationArray.push(matchingSite.address.city);
          matchingSite?.address?.state !== undefined &&
            locationArray.push(matchingSite.address.state);
          matchingSite?.address?.country !== undefined &&
            locationArray.push(matchingSite.address.country);
        }

        return {
          ...device,
          location: locationArray.join(', '),
        };
      });
      if (mergedSiteDevice.length > 0 && mergedSiteDevice.length <= 10) {
        // Retail stores don't use the switch-selection UI — every store's
        // switches are always scanned as a group. Skip the auto-populated
        // `deviceName=` params so the API isn't called with `switchnames=`.
        const isRetailSite = siteList[0]?.siteType === SiteType.Retail;
        const switchURL = isRetailSite
          ? `/${urlKey}?siteId=${siteList[0].siteId}`
          : getSwitchURL({urlKey, selectedDevices: mergedSiteDevice});
        history.replace(switchURL);
      } else {
        mergedSiteDevice.sort((a, b) => a.deviceName.localeCompare(b.deviceName));
        setTableData(mergedSiteDevice);
      }
    }
  }, [data, pending, siteList, urlKey, history]);

  return (
    <Layout
      headerContent={
        <StyledHeader>
          <LayoutHeader title={'Switch Connectivity'} icon={LayoutHeaderIconTypes.Switch} />
        </StyledHeader>
      }
    >
      <Container>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SiteSearchSelectionPrompt
              setSwitchError={() => {}}
              clickHandler={handleSiteSelection}
            />
          </Grid>
          {dataLoaded && (
            <Grid item xs={12}>
              <StyledSGBody>
                Showing {tableData.length} results for <strong>{siteList[0].siteName}</strong>
              </StyledSGBody>
              <SGBody>
                Click on any individual switch stack name to view or select multiple switch stacks
                by using the checkboxes
              </SGBody>
            </Grid>
          )}
          {dataLoaded && (
            <Grid item xs={12}>
              <SiteSearchResultsTable
                loaded={true}
                data={tableData}
                handleDeviceSelection={handleDeviceSelection}
              />
            </Grid>
          )}
          {dataLoaded && (
            <Grid item xs={12}>
              <ViewSelectionButton selectedDevices={selectedDevices} />
            </Grid>
          )}
        </Grid>
      </Container>
    </Layout>
  );
}

export default SiteSearch;
